import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { CreateUserRequest, UserRoles } from '@/models/company';
import { AddUserDialog } from './AddUserDialog';
import { Validator, useErrorHandling } from '@/utils/errorHandling';
import { PrimaryButton } from '@/components/shared/Button';
import { useUsersMutations } from '@/services/usersMutations';
import { useUser } from '@/contexts/user/UserContext';

type AddUserFormProps = {
    onUserListChange: () => void;
    name: string;
};

export const AddUserForm = (props: AddUserFormProps) => {
    const { t } = useTranslation();
    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();
    const { createUser } = useUsersMutations();
    const { user } = useUser();
    const [open, setOpen] = useState(false);
    const [validator, setValidator] = useState(new Validator());

    const onClickOpen = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    const onSubmit = (request: CreateUserRequest, onAdd: () => void): void => {
        createUser.mutate(request, {
            onSuccess: () => {
                setAlert(t('add_user.success'), 'success');
                setOpen(false);
                setValidator(validator.clearErrors());
                props.onUserListChange();
                onAdd();
            },
            onError: (error) =>
                backendErrorHandler(undefined, (_e, errorDetails) => {
                    setValidator(new Validator(errorDetails?.errors));
                })(error)
        });
    };

    // Mirrors the server-side gate on CreateUser_V2: global admin, active company Admin, or group
    // admin over this company. A Manager has nothing left here it is allowed to create, now that
    // the dialog only grants Backoffice access.
    const canCreateBackofficeUsers = [
        UserRoles.SuperAdmin,
        UserRoles.Admin,
        UserRoles.GroupAdmin
    ].includes(user.role);

    if (!canCreateBackofficeUsers) {
        return null;
    }

    return (
        <>
            <PrimaryButton variant="contained" onClick={onClickOpen}>
                {props?.name}
            </PrimaryButton>

            <AddUserDialog
                validator={validator}
                setValidator={setValidator}
                open={open}
                onClose={onClose}
                onSubmit={onSubmit}
                isSubmitting={createUser.isPending}
            />
        </>
    );
};
